import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaGamepad, FaTrophy, FaUsers, FaStream } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext.js';

const Home = () => {
  const { currentUser, userDbData, loading } = useAuth();

  useEffect(() => {
    console.log('Home component mounted, auth state:', { 
      currentUser: currentUser ? { uid: currentUser.uid, email: currentUser.email } : null,
      userDbData: userDbData ? { username: userDbData.username } : null,
      loading 
    });
  }, [currentUser, userDbData, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-harmony-blue flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-harmony-orange"></div>
      </div>
    );
  }

  if (currentUser && currentUser.uid) {
    const displayName = userDbData?.username || 
                       userDbData?.displayName || 
                       currentUser.displayName || 
                       currentUser.email || 
                       'User';
                       
    console.log('Rendering authenticated home view for user:', displayName);
    return (
      <div className="min-h-screen bg-harmony-blue">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-3xl font-bold text-harmony-white mb-6"
          >
            Welcome back, {displayName}!
          </motion.h1>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Live Streams */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-harmony-blue-dark p-6 rounded-lg border border-harmony-white/10"
            >
              <h2 className="text-xl font-semibold text-harmony-white mb-4">Live Now</h2>
              <div className="text-harmony-white/60 mb-4">
                Check out who&apos;s streaming right now
              </div>
              <Link 
                to="/browse" 
                className="inline-block px-4 py-2 bg-harmony-orange text-harmony-white rounded-lg hover:bg-harmony-orange/90 transition-colors"
              >
                Browse Streams
              </Link>
            </motion.div>

            {/* Your Stream */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-harmony-blue-dark p-6 rounded-lg border border-harmony-white/10"
            >
              <h2 className="text-xl font-semibold text-harmony-white mb-4">Start Streaming</h2>
              <div className="text-harmony-white/60 mb-4">
                Ready to go live? Start your stream now
              </div>
              <Link 
                to="/live" 
                className="inline-block px-4 py-2 bg-harmony-orange text-harmony-white rounded-lg hover:bg-harmony-orange/90 transition-colors"
              >
                Go Live
              </Link>
            </motion.div>

            {/* Tournaments */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-harmony-blue-dark p-6 rounded-lg border border-harmony-white/10"
            >
              <h2 className="text-xl font-semibold text-harmony-white mb-4">Tournaments</h2>
              <div className="text-harmony-white/60 mb-4">
                Join upcoming tournaments and compete
              </div>
              <Link 
                to="/tournaments" 
                className="inline-block px-4 py-2 bg-harmony-orange text-harmony-white rounded-lg hover:bg-harmony-orange/90 transition-colors"
              >
                View Tournaments
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  console.log('Rendering public home view');
  return (
    <div className="min-h-screen bg-harmony-blue">
      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative h-[600px] flex items-center justify-center bg-harmony-blue-dark"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-harmony-blue-dark/50 to-harmony-blue-dark"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <motion.h1 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold text-harmony-white mb-6"
          >
            Welcome to <span className="text-harmony-orange">StreamerWarZ</span>
          </motion.h1>
          <motion.p 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-xl md:text-2xl text-harmony-white/80 mb-8"
          >
            The ultimate platform for competitive streaming and gaming entertainment
          </motion.p>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <Link 
              to="/register" 
              className="px-8 py-3 bg-harmony-orange text-harmony-white rounded-lg text-lg font-semibold hover:bg-harmony-orange/90 transition-colors"
            >
              Get Started
            </Link>
            <Link 
              to="/browse" 
              className="px-8 py-3 bg-harmony-blue/20 text-harmony-white rounded-lg text-lg font-semibold hover:bg-harmony-blue/30 transition-colors"
            >
              Browse Streams
            </Link>
          </motion.div>
        </div>
      </motion.div>

      {/* Features Section */}
      <div className="py-20 bg-harmony-blue-dark">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-harmony-blue/20 p-6 rounded-lg border border-harmony-white/10"
            >
              <FaStream className="text-4xl text-harmony-orange mb-4" />
              <h3 className="text-xl font-semibold text-harmony-white mb-3">Live Streams</h3>
              <p className="text-harmony-white/60">Watch your favorite streamers compete in real-time battles</p>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-harmony-blue/20 p-6 rounded-lg border border-harmony-white/10"
            >
              <FaTrophy className="text-4xl text-harmony-orange mb-4" />
              <h3 className="text-xl font-semibold text-harmony-white mb-3">Tournaments</h3>
              <p className="text-harmony-white/60">Join or watch competitive gaming tournaments with real prizes</p>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-harmony-blue/20 p-6 rounded-lg border border-harmony-white/10"
            >
              <FaUsers className="text-4xl text-harmony-orange mb-4" />
              <h3 className="text-xl font-semibold text-harmony-white mb-3">Community</h3>
              <p className="text-harmony-white/60">Connect with other gamers and build your following</p>
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="bg-harmony-blue/20 p-6 rounded-lg border border-harmony-white/10"
            >
              <FaGamepad className="text-4xl text-harmony-orange mb-4" />
              <h3 className="text-xl font-semibold text-harmony-white mb-3">Game Integration</h3>
              <p className="text-harmony-white/60">Seamless integration with your favorite games</p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home; 